import { useEffect, useState } from "react";
import { Search, Loader2, User, Target } from "lucide-react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { brl, formatDateTime } from "@/lib/format";

const STATUS = {
  pendente: "bg-amber-100 text-amber-800 border-amber-200",
  confirmado: "bg-emerald-100 text-emerald-800 border-emerald-200",
  recusado: "bg-rose-100 text-rose-800 border-rose-200",
};

export default function SearchPanel() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/admin/busca", { params: { q } }).then((r) => setRes(r.data)).catch(() => setRes({ contribuicoes: [], metas: [] })).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div data-testid="search-panel">
      <div className="relative max-w-xl">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          data-testid="search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar pessoa, telefone, meta ou referente..."
          className="pl-12 h-14 text-base rounded-xl bg-white"
          autoFocus
        />
      </div>
      <p className="mt-2 text-sm text-slate-500">Digite pelo menos 2 caracteres.</p>

      {loading && <div className="flex justify-center py-10"><Loader2 className="w-7 h-7 animate-spin text-blue-800" /></div>}

      {res && !loading && (
        <div className="mt-6 space-y-6">
          <section>
            <h3 className="font-heading text-base font-bold text-slate-700 mb-3 inline-flex items-center gap-2"><Target className="w-4 h-4" /> Metas ({res.metas.length})</h3>
            {res.metas.length === 0 ? <Empty /> : (
              <div className="space-y-2">
                {res.metas.map((m) => (
                  <div key={m.id} data-testid={`search-meta-${m.id}`} className="bg-white rounded-xl border border-slate-200 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-900">{m.referente}</p>
                      <p className="text-xs text-slate-500">{m.status === "pago" ? "Concluída" : "Em aberto"}{m.arquivada ? " · arquivada" : !m.ativo ? " · não publicada" : ""}</p>
                    </div>
                    <p className="text-sm"><span className="font-bold text-emerald-700">{brl(m.valor_arrecadado)}</span> / {brl(m.valor_total)}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
          <section>
            <h3 className="font-heading text-base font-bold text-slate-700 mb-3 inline-flex items-center gap-2"><User className="w-4 h-4" /> Contribuições ({res.contribuicoes.length})</h3>
            {res.contribuicoes.length === 0 ? <Empty /> : (
              <div className="space-y-2">
                {res.contribuicoes.map((c) => (
                  <div key={c.id} data-testid={`search-contrib-${c.id}`} className="bg-white rounded-xl border border-slate-200 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">{c.nome} <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${STATUS[c.status]}`}>{c.status.toUpperCase()}</span></p>
                      <p className="text-xs text-slate-500">{c.whatsapp && `${c.whatsapp} · `}{c.meta_referente} · {formatDateTime(c.created_at)}</p>
                    </div>
                    <p className="font-heading font-extrabold text-blue-900">{brl(c.valor)}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function Empty() {
  return <div className="bg-white rounded-xl border border-dashed border-slate-200 px-4 py-4 text-sm text-slate-400">Nada encontrado.</div>;
}
